import { Component, HostBinding, OnInit, OnDestroy, Inject } from '@angular/core';
import { DatePipe, DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';

import { combineLatest, Observable, Subscription, TeardownLogic } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { ApplicationService } from 'services/application/application.service';
import { AuthService } from 'services/auth/auth.service';
import { ColorThemeService } from 'services/colortheme/colortheme.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { UserService } from 'services/user/user.service';
import { UserInfo } from 'services/user/user.service.class';
import { BUILD_NOTES, BUILD_TIME, VERSION_NUMBER } from 'services/version/version.service';
import { UnsavedChangesService } from 'routes/routes.guard';

import { ConfirmationDialogService } from 'dialogs/confirmation';
import { MessageBoxDialogService } from 'dialogs/messagebox';
import { SettingsDialogService } from 'dialogs/settings';
import { RoutePaths } from 'routes/route.roles';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    @HostBinding('class.smalldisplay') SmallDisplay: boolean;

    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    AboutFn: () => Observable<boolean>;
    LogoutFn: () => Observable<boolean>;

    BuildNotes: string;
    BuildTime: string;
    CurrentRole: string;
    DevSite: boolean;
    LastSizeCode: string;
    LastSmallDisplay: boolean;
    SizeCode: string;
    UpdateAvailable: boolean;
    User: UserInfo | null;
    VersionLoaded: string;
    ViewReady: boolean;

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private colorThemeSvc: ColorThemeService,
        private datePipe: DatePipe,
        private errorService: ErrorService,
        private router: Router,
        private screenSizeSvc: ScreenSizeService,
        private unsavedChangesSvc: UnsavedChangesService,
        private userService: UserService,
        private msgBoxDialog: MessageBoxDialogService,
        private confBoxDialog: ConfirmationDialogService,
        private settingsBoxDlgSvc: SettingsDialogService,
        @Inject(DOCUMENT) private document: Document
    ) {
        const self = this;

        this.AboutFn = this.About.bind(this);
        this.BuildNotes = BUILD_NOTES;
        this.CurrentRole = '';
        this.DevSite = false;
        this.LastSizeCode = '';
        this.LogoutFn = this.Logout.bind(this);
        this.SizeCode = '';
        this.SmallDisplay = false;
        this.LastSmallDisplay = false;
        this.UpdateAvailable = false;
        this.User = null;
        this.VersionLoaded = VERSION_NUMBER;
        this.ViewReady = false;

        const btime = BUILD_TIME && new Date(BUILD_TIME);
        this.BuildTime = (btime && this.datePipe.transform(btime, 'MMM. dd, yyyy h:mm a')) || '';

        // Subscribe to the color theme service, so we can set the CSS accordingly to changes.
        this.subrelease = this.colorThemeSvc.CurrentThemeSub.subscribe((theme) => {
            if (theme != null) {
                // Set the theme accordingly.
                this.document.body.classList.remove(theme === 'my-light-theme' ? 'my-dark-theme' : 'my-light-theme');
                this.document.body.classList.add(theme);
            }
        });

        // Subscribe to checks for a version upgrade
        self.subrelease = self.authService.HasVersionUpdate.subscribe((value) => {
            // Set the upgrade alert banner (or not)
            self.UpdateAvailable = value;
        });

        // Subscribe to media changes
        self.subrelease = self.screenSizeSvc.ScreenSizeSub.subscribe((size) => {
            self.SmallDisplay = size.IsSmallDisplay;
            self.SizeCode = size.Alias;

            self.LastSmallDisplay = self.SmallDisplay;
        });

        // Subscribe to user object changes
        self.subrelease = self.userService.UserSubject.subscribe((_user) => (self.User = _user));

        // Subscribe to system online status updates. If the system goes offline, this may cause us to reroute to the offline route.
        self.subrelease = self.authService.SystemOnlineSubject.pipe(distinctUntilChanged((p, c) => p.Status === c.Status)).subscribe(
            (status) => {
                if (status?.Status) {
                    // Load everything needed for the app to run initially.
                    combineLatest([
                        self.appService.GetAllSchools(),
                        self.appService.GetCurrentSchoolYear(),
                        self.appService.GetAllSchoolYears(),
                    ]).subscribe(
                        (_) => {
                            self.ViewReady = true;

                            // Route to the loading of the login panel controls if the route is currently unrouted or offline.
                            if (!this.router.url || this.router.url === '/' || this.router.url === '/offline') {
                                // Go to the login route.
                                console.log('about to reroute to login');
                                self.router.navigate([`/${RoutePaths.Login}`]);
                                return;
                            }

                            // If we're trying to route to the login route, but we have a user object loaded, go to the home page instead.
                            if (this.router.url.indexOf('login') >= 0 && !!self.userService?.User?.AuthToken) {
                                this.router.navigate([`/${RoutePaths.Home}`]);
                            }
                        },
                        (err) => {
                            self.ViewReady = true;
                            self.errorService.HandleServiceError(err);
                        }
                    );
                } else {
                    // If the system is offline, AND if there is no current user authtoken, go to the down/offline route.
                    self.ViewReady = true;

                    if (!self.userService?.User?.AuthToken) {
                        self.router.navigate([`/${RoutePaths.Offline}`]);
                    }
                }
            }
        );

        // Conduct an initial test of the connection, to set the proper status.
        this.authService.TestConnection().subscribe(
            (result) => {
                this.authService.SetSystemOnlineStatus(result);
            },
            (err) => {
                // A failure would indicate the system is offline.
                this.authService.SetSystemOnlineStatus({
                    Status: false,
                    SystemMessage: '',
                    SSTVersion: VERSION_NUMBER,
                });
            }
        );
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
    }

    ngOnInit(): void {}

    // #region About Method

    About(): Observable<boolean> {
        const self = this;

        let msg =
            `The Schools Support Tracker system is a product of the Polk County Public Schools system. The system provides a single location for authorized district staff to record information related to assisting schools in working towards their individual school improvement plans as well as the district's improvement initiatives.<br /><br />` +
            `<strong>Schools Support Tracker Version: </strong>${self.VersionLoaded}<br />
    <strong>Build Date/Time: </strong>${self.BuildTime}`;

        if (self.BuildNotes) {
            msg += `<br /><strong>Build Notes: </strong><br />` + self.BuildNotes;
        }

        // return self.dialogService.OpenMessageBoxDialog({
        return self.msgBoxDialog.Open({
            TitleText: 'About Schools Support Tracker',
            MessageText: msg,
        });
    }

    // #endregion

    // #region Get Update Method

    GetUpdate(): void {
        // Get a confirmation
        // this.dialogService
        this.confBoxDialog
            .Open({
                // .OpenConfirmationDialog({
                TitleText: 'Site Update Available',
                MessageText:
                    `An update is available to the Schools Support Tracker system. Updates typically fix issues reported ` +
                    `by users or offer new features. If you are encountering problems with the site, it is recommended that ` +
                    `you load the update for the website at this time.<br /><br /><strong>Note: </strong>Loading the update ` +
                    `will refresh your page and you will lose any unsubmitted changes.`,
                OKText: 'Load Update',
                CancelText: 'CANCEL',
            })
            .subscribe((res) => {
                if (res === true) {
                    // User is opting to load the update. Refresh the page without caching.
                    window.location.href = `${window.location.protocol}//${window.location.host}`;
                }
            });
    }

    // #endregion

    // #region Go Home Method

    GoHome(): void {
        if (this.userService.User == null || this.userService.User.AuthToken == null) {
            console.log('gohome()');
            // this.router.navigate(['/login']);
        } else {
            this.router.navigate(['/home']);
        }
    }

    // #endregion

    // #region Logout Method

    Logout(): Observable<boolean> {
        const self = this;

        // If we don't have a tracker in place, and we're on xs size code, we need to ensure a dialog will open.
        let confObs: Observable<boolean> | null = null;

        if (!self.unsavedChangesSvc.HasTracker() && self.SizeCode === 'xs') {
            confObs = null; // Ensures it will defer to dialog.
        } else {
            confObs = self.unsavedChangesSvc.CheckTracker?.(); // This will attempt to use a check tracker.
        }

        return (
            confObs ||
            // self.dialogService.OpenConfirmationDialog({
            self.confBoxDialog.Open({
                TitleText: 'Confirmation Requested',
                MessageText:
                    'You are about to log out of the Schools Support Tracker system. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            })
        ).pipe(
            tap((result) => {
                if (result === true) {
                    // The tracker says it's okay to logout, Go ahead and logout without further confirmation.
                    // Disable change tracking first.
                    self.unsavedChangesSvc.ClearTracking();

                    // Now perform the logout.
                    self.authService.Logout();
                }
            })
        );
    }

    // #endregion

    // #region Toolbar About Method

    ToolbarAbout(): void {
        this.About().subscribe();
    }

    // #endregion

    // #region Toolbar Logout Method

    ToolbarLogout(): void {
        this.Logout().subscribe();
    }

    // #endregion

    // #region Toolbar Settings

    ToolbarSettings(): void {
        this.settingsBoxDlgSvc
            .Open({
                MessageText: 'Use the option provided below to modify the application settings:',
                CurrentTheme: this.colorThemeSvc.CurrentTheme,
            })
            .subscribe();
    }

    // #endregion
}
